import React from 'react';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { SearchFolder } from '../useData';
import type { SearchFolderSummaryStructured } from '../getSummarySentence';
import {
  FOLDER_KIND_ICONS,
  FOLDER_KIND_LABELS,
  folderKindAccentClass,
  folderKindBadgeClass,
  resolveFolderKind,
} from '../searchFolderKindUi';
import ShareSearchFolderButton from './ShareSearchFolderButton';

const FOLDER_LABELS: Record<string, string> = {
  '': 'All folders',
  INBOX: 'Inbox',
  DRAFT: 'Draft',
  SENT: 'Sent',
};

function getFolderLabel(folder: string | null | undefined): string {
  const key = (folder ?? '').trim();
  return FOLDER_LABELS[key] ?? (key || 'All folders');
}

interface SmartFolderCardProps {
  folder: SearchFolder;
  summary: SearchFolderSummaryStructured;
  accountsLabel: string;
  conditionCount: number;
  detailHref: string;
  editHref: string;
  onDelete: () => void;
  deleting: boolean;
}

const SmartFolderCard: React.FC<SmartFolderCardProps> = ({
  folder,
  summary,
  accountsLabel,
  conditionCount,
  detailHref,
  editHref,
  onDelete,
  deleting,
}) => {
  const { colors } = useTheme();
  const folderKind = resolveFolderKind(folder.folderKind);
  const KindIcon = FOLDER_KIND_ICONS[folderKind];
  const visibleConditions = summary.conditionLines.slice(0, 3);
  const hiddenConditionCount = summary.conditionLines.length - visibleConditions.length;

  return (
    <article
      className={cn(
        'overflow-hidden rounded-xl border border-l-4 transition-shadow hover:shadow-md',
        colors.border,
        colors.surface,
        folderKindAccentClass(folderKind),
      )}
    >
      <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-start lg:justify-between">
        <Link to={detailHref} className="group flex min-w-0 flex-1 items-start gap-4">
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <KindIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" aria-hidden />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn('truncate text-base font-semibold group-hover:underline', colors.text)}>
                {folder.name}
              </h2>
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                  folderKindBadgeClass(folderKind),
                )}
              >
                {FOLDER_KIND_LABELS[folderKind]}
              </span>
              {summary.matchLabel ? (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                    colors.border,
                    colors.textMuted,
                  )}
                >
                  {summary.matchLabel}
                </span>
              ) : null}
            </div>

            <p className={cn('mt-1 text-sm leading-relaxed', colors.textSecondary)}>{summary.headline}</p>

            {summary.periodLine ? (
              <p className={cn('mt-1 text-xs', colors.textMuted)}>{summary.periodLine}</p>
            ) : null}

            {visibleConditions.length > 0 ? (
              <ul className={cn('mt-3 space-y-1 rounded-lg border px-3 py-2 text-xs', colors.border, colors.surfaceHover)}>
                {visibleConditions.map((line, index) => (
                  <li key={index} className={cn('flex items-start gap-2', colors.textSecondary)}>
                    <span className="mt-0.5 shrink-0 text-indigo-500 dark:text-indigo-400">•</span>
                    <span className="min-w-0 break-words">{line}</span>
                  </li>
                ))}
                {hiddenConditionCount > 0 ? (
                  <li className={cn('text-[11px]', colors.textMuted)}>+{hiddenConditionCount} more conditions</li>
                ) : null}
              </ul>
            ) : null}
          </div>
        </Link>

        <div className="flex shrink-0 items-center gap-1 self-end lg:self-start">
          <Link
            to={detailHref}
            title="View smart folder"
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              colors.text,
              colors.interactiveHover,
            )}
          >
            <Eye className="h-4 w-4" />
          </Link>
          <Link
            to={editHref}
            title="Edit smart folder"
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              colors.text,
              colors.interactiveHover,
            )}
          >
            <Pencil className="h-4 w-4" />
          </Link>
          <ShareSearchFolderButton folder={folder} iconOnly />
          <button
            type="button"
            title="Delete smart folder"
            disabled={deleting}
            onClick={onDelete}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              'text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/30',
              deleting && 'cursor-not-allowed opacity-50',
            )}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className={cn('grid grid-cols-2 gap-px border-t sm:grid-cols-4', colors.border, colors.surfaceHover)}>
        <StatCell label="Order" value={String(folder.displayOrder)} />
        <StatCell label="Conditions" value={String(conditionCount)} />
        <StatCell label="Accounts" value={accountsLabel} />
        <StatCell label="Folder scope" value={getFolderLabel(folder.folder)} />
      </div>

      {folder.ownerName ? (
        <div className={cn('border-t px-4 py-2.5 text-xs sm:px-5', colors.border, colors.textMuted)}>
          Owner: <span className={cn('font-medium', colors.textSecondary)}>{folder.ownerName}</span>
        </div>
      ) : null}
    </article>
  );
};

function StatCell({ label, value }: { label: string; value: string }) {
  const { colors } = useTheme();
  return (
    <div className={cn('px-4 py-3', colors.surface)}>
      <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>{label}</p>
      <p className={cn('mt-1 truncate text-sm font-semibold', colors.text)} title={value}>
        {value}
      </p>
    </div>
  );
}

export default SmartFolderCard;
